import React, { useState } from "react";
import { Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { ScrollAnimation } from "../ScrollAnimation";

const testimonialsMaria = [
  "Profissional excelente e muito humana — me senti ouvida e compreendida desde a primeira consulta.",
  "Dra. Maria Inês apresentou humanidade e sensibilidade diante da minha ansiedade; finalmente encontrei um tratamento que funciona.",
  "Extremamente atenciosa, sempre disposta a esclarecer dúvidas e dar suporte contínuo; graças ao acompanhamento dela, minha qualidade de vida melhorou muito.",
  "Método de atendimento eficiente e acolhedor, onde pude falar com calma sobre todos os meus medos e dificuldades — me deu paz e esperança.",
  "Profissional de altíssima competência; pontual, clara nas orientações e muito empática com meus sentimentos.",
  "Transformou meu tratamento: me mostrou caminhos para lidar com a depressão de forma realista e amorosa — sinto evolução como nunca antes.",
];

const testimonialsCarlos = [
  "Ótimo profissional!!! Tenho depressão há 20 anos. Único profissional que realmente me ajudou com a medicação correta para meu grau de depressão. Muito obrigada doutor Carlos!!!",
  "Excelente Profissional e Ser Humano; sensível às queixas que me levaram até a consulta presencial…",
  "O Dr. Carlos é extremamente atencioso, presta suporte em todos os momentos em que preciso e está me ajudando muito a melhorar. Tem muito conhecimento técnico e passa muita confiança.",
  "Método de consulta prático e eficiente onde se tem oportunidade de forma tranquila… relatar todas as dúvidas e necessidades.",
  "Excelente profissional, sempre pontual e atencioso.",
  "Um profissional diferenciado. Experiente, humano, eficaz no tratamento. Após ter iniciado o tratamento atual com o Dr. Carlos, teve acentuada melhora, como nunca antes; além de o equilíbrio e a harmonia estarem de volta à família.",
];

function TestimonialCarousel({ testimonials, doctorName, attribution, doctoraliaUrl }) {
  const [activeIndex, setActiveIndex] = useState(0);

  const goToPrev = () => setActiveIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  const goToNext = () => setActiveIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));

  return (
    <div className="flex flex-col h-full">
      <h3 className="font-sans text-xl sm:text-2xl font-bold text-[#C2A44A] text-center mb-6">{doctorName}</h3>
      <div className="bg-white border border-[#ACC996]/25 rounded-2xl p-6 sm:p-8 shadow-md flex-1 min-h-[220px] flex flex-col">
        <Quote className="w-8 h-8 text-[#C2A44A] mb-4 flex-shrink-0" />
        <p className="font-serif text-base sm:text-lg text-[#3d3d3d] leading-relaxed italic flex-1">
          "{testimonials[activeIndex]}"
        </p>
        <div className="border-t border-[#ACC996]/30 pt-4 mt-4">
          <p className="font-sans text-sm text-[#698658]">— {attribution}</p>
        </div>
      </div>

      <div className="flex items-center justify-center gap-3 mt-6">
        <button
          onClick={goToPrev}
          className="w-10 h-10 rounded-full border border-[#ACC996]/40 flex items-center justify-center text-[#698658] hover:bg-[#ACC996]/10 transition-colors duration-300"
          aria-label="Anterior"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex gap-2">
          {testimonials.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setActiveIndex(idx)}
              className={`h-2 rounded-full transition-all duration-300 ${
                activeIndex === idx ? "w-6 bg-[#698658]" : "w-2 bg-[#ACC996]/40 hover:bg-[#ACC996]/60"
              }`}
              aria-label={`Depoimento ${idx + 1}`}
            />
          ))}
        </div>
        <button
          onClick={goToNext}
          className="w-10 h-10 rounded-full border border-[#ACC996]/40 flex items-center justify-center text-[#698658] hover:bg-[#ACC996]/10 transition-colors duration-300"
          aria-label="Próximo"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <a
        href={doctoraliaUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="font-sans text-base sm:text-lg font-semibold text-[#204613] hover:text-[#C2A44A] transition-colors duration-300 text-center mt-6 inline-flex items-center justify-center gap-2"
      >
        Ver mais depoimentos na Doctoralia
        <span className="text-xl">→</span>
      </a>
    </div>
  );
}

export default function Testimonials() {
  return (
    <section id="depoimentos" className="py-20 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[#f5f0e4]">
      <div className="max-w-6xl mx-auto">
        <ScrollAnimation animation="fade-up">
          <span className="font-sans text-sm font-semibold text-[#C2A44A] tracking-wider uppercase mb-3 block text-center">Depoimentos</span>
        </ScrollAnimation>
        <ScrollAnimation animation="fade-up" delay={100}>
          <h2 className="font-sans text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#204613] mb-8 text-center text-balance max-w-4xl mx-auto leading-tight">
            Histórias de transformação e esperança
          </h2>
        </ScrollAnimation>
        <ScrollAnimation animation="fade-up" delay={150}>
          <p className="font-serif text-base sm:text-lg text-[#3d3d3d] mb-12 text-center max-w-2xl mx-auto">
            Conheça as experiências de quem encontrou um caminho de cura e bem-estar através do nosso cuidado.
          </p>
        </ScrollAnimation>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          <ScrollAnimation animation="fade-right" delay={200}>
            <TestimonialCarousel
              testimonials={testimonialsMaria}
              doctorName="Dra. Maria Inês Mesquita"
              attribution="Paciente de Maria Inês"
              doctoraliaUrl="https://www.doctoralia.com.br/maria-ines-mesquita/psiquiatra/florianopolis#profile-reviews"
            />
          </ScrollAnimation>
          <ScrollAnimation animation="fade-left" delay={200}>
            <TestimonialCarousel
              testimonials={testimonialsCarlos}
              doctorName="Dr. Carlos Heitor Mesquita"
              attribution="Paciente do Dr. Carlos"
              doctoraliaUrl="https://www.doctoralia.com.br/carlos-heitor-mesquita/psiquiatra/florianopolis"
            />
          </ScrollAnimation>
        </div>
      </div>
    </section>
  );
}
